<template>
    <div class="pdf-viewer-container">
        <!-- 页面导航控件 -->
        <div class="navigation-controls">
            <div class="page-info">
                <button @click="prevPage" :disabled="currentPage <= 1" class="nav-btn">
                    ← 上一页
                </button>
                <span class="page-counter">
          第 {{ currentPage }} 页 / 共 {{ totalPages }} 页
        </span>
                <button @click="nextPage" :disabled="currentPage >= totalPages" class="nav-btn">
                    下一页 →
                </button>
            </div>

            <div class="page-jump">
                <input
                        v-model.number="jumpPageInput"
                        type="number"
                        min="1"
                        :max="totalPages"
                        placeholder="输入页码"
                        class="page-input"
                        @keyup.enter="goToPage(jumpPageInput)"
                >
                <button @click="goToPage(jumpPageInput)" class="jump-btn">
                    跳转
                </button>
            </div>

            <!-- 高亮快速跳转 -->
            <div class="highlight-jump" v-if="highlightsList.length > 0">
                <select v-model="selectedHighlight" @change="jumpToSelectedHighlight" class="highlight-select">
                    <option value="">跳转到高亮...</option>
                    <option
                            v-for="(highlight, index) in highlightsList"
                            :key="index"
                            :value="index"
                    >
                        第 {{ highlight.page }} 页: {{ highlight.text.substring(0, 30) }}...
                    </option>
                </select>
            </div>
        </div>

        <!-- PDF 页面区域 -->
        <div class="pdf-content">
<!--            &lt;!&ndash; 页面缩略图导航 &ndash;&gt;-->
<!--            <div class="thumbnail-sidebar" v-if="showThumbnails">-->
<!--                <div-->
<!--                        v-for="page in pages"-->
<!--                        :key="page.pageNumber"-->
<!--                        :class="['thumbnail', { active: page.pageNumber === currentPage }]"-->
<!--                        @click="goToPage(page.pageNumber)"-->
<!--                >-->
<!--                    <div class="thumbnail-canvas">-->
<!--                        <canvas :ref="(el) => setThumbnailRef(el, page.pageNumber)"></canvas>-->
<!--                    </div>-->
<!--                    <span class="thumbnail-label">第 {{ page.pageNumber }} 页</span>-->
<!--                </div>-->
<!--            </div>-->

            <!-- 主内容区域 -->
            <div class="main-content" ref="mainContent">
                <div
                        v-for="page in pages"
                        :key="page.pageNumber"
                        :id="`page-${page.pageNumber}`"
                        class="page-wrapper"
                >
                    <div class="page-header">
                        <span class="page-number-badge">第 {{ page.pageNumber }} 页</span>
                    </div>
                    <div class="page-content">
                        <canvas :ref="(el) => setCanvasRef(el, page.pageNumber)"></canvas>
                        <div class="highlight-layer" :ref="(el) => setHighlightLayerRef(el, page.pageNumber)"></div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 浮动跳转按钮 -->
        <div class="floating-controls" v-if="showFloatingControls">
            <button @click="goToPage(1)" class="floating-btn" title="第一页">
                ⏮
            </button>
            <button @click="prevPage" class="floating-btn" title="上一页">
                ⏪
            </button>
            <div class="page-display">{{ currentPage }}</div>
            <button @click="nextPage" class="floating-btn" title="下一页">
                ⏩
            </button>
            <button @click="goToPage(totalPages)" class="floating-btn" title="最后一页">
                ⏭
            </button>
        </div>
    </div>
</template>

<script setup>
    import { ref, onMounted, watch, nextTick, defineProps, defineEmits, defineExpose } from 'vue';
    import * as pdfjsLib from 'pdfjs-dist';
    const scale = 1.15;
    // 配置 PDF.js
    pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`;

    const props = defineProps({
        pdfUrl: String,
        mineruData: Object,
        activeSpan: Object,
        initialPage: {
            type: Number,
            default: 1
        },
        showThumbnails: {
            type: Boolean,
            default: true
        },
        showFloatingControls: {
            type: Boolean,
            default: true
        }
    });

    const emit = defineEmits(['page-changed', 'page-jump']);

    // 响应式数据
    const pages = ref([]);
    const canvasRefs = ref({});
    const thumbnailRefs = ref({});
    const highlightLayerRefs = ref({});
    const mainContent = ref(null);
    const currentPage = ref(1);
    const totalPages = ref(0);
    const jumpPageInput = ref(null);
    const selectedHighlight = ref('');
    const highlightsList = ref([]);

    // 设置引用
    const setCanvasRef = (el, pageNumber) => {
        if (el) canvasRefs.value[pageNumber] = el;
    };

    // const setThumbnailRef = (el, pageNumber) => {
    //     if (el) thumbnailRefs.value[pageNumber] = el;
    // };

    const setHighlightLayerRef = (el, pageNumber) => {
        if (el) highlightLayerRefs.value[pageNumber] = el;
    };

    // 加载 PDF
    const loadPdf = async () => {
        try {
            console.log('开始加载 PDF:', props.pdfUrl);
            const loadingTask = pdfjsLib.getDocument(props.pdfUrl);
            const pdfDoc = await loadingTask.promise;
            totalPages.value = pdfDoc.numPages;
            pages.value = [];

            console.log(`PDF 总页数: ${totalPages.value}`);

            // 加载所有页面
            for (let i = 1; i <= pdfDoc.numPages; i++) {
                const page = await pdfDoc.getPage(i);
                pages.value.push({
                    pageNumber: i,
                    page
                });

                await nextTick();
                await renderPage(page, i);
                await renderThumbnail(page, i);
            }

            // 构建高亮列表
            buildHighlightsList();

            // 跳转到初始页面
            if (props.initialPage) {
                await nextTick();
                goToPage(props.initialPage);
            }

        } catch (error) {
            console.error('加载 PDF 失败:', error);
        }
    };

    // 渲染主页面
    const renderPage = async (page, pageNumber) => {
        const canvas = canvasRefs.value[pageNumber];
        if (!canvas) {
            console.warn(`Canvas for page ${pageNumber} not found`);
            return;
        }

        const viewport = page.getViewport({ scale });
        const context = canvas.getContext('2d');

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderContext = {
            canvasContext: context,
            viewport: viewport
        };

        try {
            await page.render(renderContext).promise;
            console.log(`页面 ${pageNumber} 渲染完成`);

            // 渲染高亮
            // renderHighlights(pageNumber, viewport);
        } catch (error) {
            console.error(`渲染页面 ${pageNumber} 失败:`, error);
        }
    };

    // 渲染缩略图
    const renderThumbnail = async (page, pageNumber) => {
        const canvas = thumbnailRefs.value[pageNumber];
        if (!canvas) return;

        const viewport = page.getViewport({ scale: 0.3 });
        const context = canvas.getContext('2d');

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderContext = {
            canvasContext: context,
            viewport: viewport
        };

        await page.render(renderContext).promise;
    };

    // // 渲染高亮
    // const renderHighlights = (pageNumber, viewport) => {
    //     const highlightLayer = highlightLayerRefs.value[pageNumber];
    //     if (!highlightLayer || !props.mineruData) return;
    //
    //     highlightLayer.innerHTML = '';
    //
    //     const spans = props.mineruData.pages?.filter(item => (item.page + 1) === pageNumber);
    //     spans.map(span=>{
    //         if (!span) return;
    //         if (span.bbox && span.paragraph && span.paragraph.trim()) {
    //             createHighlightElement(span, viewport, highlightLayer, pageNumber);
    //         }
    //     })
    // };

    // 渲染高亮
    const renderHighlight = (span) => {
        let pageNumber = span.page + 1;
        const highlightLayer = highlightLayerRefs.value[pageNumber];
        if (!highlightLayer) return;

        highlightLayer.innerHTML = '';

        const viewport = pages.value[span.page + 1]?.page.getViewport({ scale });
        if (!span) return;
        if (span.bbox && span.paragraph && span.paragraph.trim()) {
            createHighlightElement(span, viewport, highlightLayer, pageNumber);
        }
    };

    // 创建高亮元素
    const createHighlightElement = (span, viewport, container, pageNumber) => {
        const [x0, y0, x1, y1] = span.bbox;
        const pdfBoundingBox = [x0, y0, x1, y1];
        const viewportBoundingBox = viewport.convertToViewportRectangle(pdfBoundingBox);

        const [left, bottom, right, top] = viewportBoundingBox;
        const width = (right - left);
        const height = Math.abs(bottom - top);
        console.log('createHighlightElement', span);
        const highlightEl = document.createElement('div');
        highlightEl.className = 'text-highlight';
        highlightEl.style.position = 'absolute';
        highlightEl.style.left = `${left}px`;
        highlightEl.style.top = `${viewport.height - bottom}px`;
        highlightEl.style.width = `${width}px`;
        highlightEl.style.height = `${height}px`;
        highlightEl.style.backgroundColor = 'rgba(255, 235, 59, 0.4)';
        highlightEl.style.border = '1px solid rgba(255, 193, 7, 0.6)';
        highlightEl.style.borderRadius = '2px';
        highlightEl.style.pointerEvents = 'auto';
        highlightEl.style.cursor = 'pointer';
        highlightEl.style.transition = 'all 0.2s ease';

        // 添加数据属性
        highlightEl.dataset.page = pageNumber;
        highlightEl.dataset.text = span.paragraph;
        highlightEl.title = span.name + ': ' + span.paragraph;

        // 点击高亮跳转到对应页面
        highlightEl.addEventListener('click', (e) => {
            e.stopPropagation();
            goToPage(pageNumber);
        });

        // 悬停效果
        highlightEl.addEventListener('mouseenter', () => {
            highlightEl.style.backgroundColor = 'rgba(255, 235, 59, 0.6)';
            highlightEl.style.transform = 'scale(1.02)';
        });

        highlightEl.addEventListener('mouseleave', () => {
            highlightEl.style.backgroundColor = 'rgba(255, 235, 59, 0.4)';
            highlightEl.style.transform = 'scale(1)';
        });

        container.appendChild(highlightEl);
    };
    // 添加高亮
    const addHighlight = (pageNumber, span) => {
        // const highlightId = `highlight-${++highlightIdCounter}`;
        //
        const newHighlight = {
            id: span.name,
            page: pageNumber,
            text: span.text,
            bbox: span.bbox,
            timestamp: new Date().toISOString()
        };

        // activeHighlights.value.push(newHighlight);
        console.log('添加高亮:', newHighlight);
        //
        // // 触发事件
        emit('highlight-added', newHighlight);

        // 重新渲染高亮层
        goToPage(pageNumber);
        renderHighlight(span);
    };
    // 构建高亮列表用于快速跳转
    const buildHighlightsList = () => {
        if (!props.mineruData) return;

        highlightsList.value = [];
        props.mineruData.pages?.forEach(span => {
            if (span.paragraph && span.paragraph.trim()) {
                highlightsList.value.push({
                    text: span.paragraph,
                    page: span.page + 1,
                    ...span
                });
            }
        });

        console.log(`构建了 ${highlightsList.value.length} 个高亮项`);
    };

    // 页面跳转功能
    const goToPage = (pageNumber) => {
        if (!pageNumber || pageNumber < 1 || pageNumber > totalPages.value) {
            console.warn(`无效的页码: ${pageNumber}`);
            return;
        }

        console.log(`跳转到第 ${pageNumber} 页`);

        const targetElement = document.getElementById(`page-${pageNumber}`);
        if (targetElement && mainContent.value) {
            // 计算滚动位置
            const container = mainContent.value;
            // const targetRect = targetElement.getBoundingClientRect();
            // const containerRect = container.getBoundingClientRect();

            const scrollTop = targetElement.offsetTop - container.offsetTop - 20;

            // 执行滚动
            container.scrollTo({
                top: scrollTop,
                behavior: 'smooth'
            });

            // 更新当前页面
            currentPage.value = pageNumber;

            // 触发事件
            emit('page-changed', pageNumber);
            emit('page-jump', pageNumber);

            // 更新 URL hash（可选）
            window.location.hash = `page-${pageNumber}`;

            // 视觉反馈
            highlightCurrentPage(targetElement);
        } else {
            console.warn(`未找到页面元素: page-${pageNumber}`);
        }
    };

    // 高亮当前页面
    const highlightCurrentPage = (pageElement) => {
        // 移除所有现有高亮
        document.querySelectorAll('.page-wrapper.current').forEach(el => {
            el.classList.remove('current');
        });

        // 添加当前页面高亮
        pageElement.classList.add('current');
    };

    // 上一页
    const prevPage = () => {
        if (currentPage.value > 1) {
            goToPage(currentPage.value - 1);
        }
    };

    // 下一页
    const nextPage = () => {
        if (currentPage.value < totalPages.value) {
            goToPage(currentPage.value + 1);
        }
    };

    // 跳转到选中的高亮
    const jumpToSelectedHighlight = () => {
        if (selectedHighlight.value !== '') {
            const highlight = highlightsList.value[selectedHighlight.value];
            if (highlight) {
                goToPage(highlight.page);
            }
        }
        selectedHighlight.value = '';
    };

    // 监听初始页面变化
    watch(() => props.initialPage, (newPage) => {
        if (newPage && newPage !== currentPage.value) {
            nextTick(() => goToPage(newPage));
        }
    });

    // // 监听 mineruData 变化
    // watch(() => props.mineruData, () => {
    //     buildHighlightsList();
    //     // 重新渲染高亮
    //     pages.value.forEach(pageObj => {
    //         const viewport = pageObj.page.getViewport({ scale });
    //         renderHighlights(pageObj.pageNumber, viewport);
    //     });
    // }, { deep: true });

    // 监听 mineruData 变化
    watch(() => props.activeSpan, () => {
        console.log(props.activeSpan);
        renderHighlight(props.activeSpan);
    }, { deep: true });

    // 键盘快捷键
    const handleKeyPress = (event) => {
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            prevPage();
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            nextPage();
        }
    };

    // 滚动时更新当前页面
    const handleScroll = () => {
        if (!mainContent.value) return;

        // const scrollTop = mainContent.value.scrollTop;
        const pageWrappers = mainContent.value.querySelectorAll('.page-wrapper');

        for (let i = 0; i < pageWrappers.length; i++) {
            const wrapper = pageWrappers[i];
            const rect = wrapper.getBoundingClientRect();
            const containerRect = mainContent.value.getBoundingClientRect();

            // 如果页面顶部进入视口
            if (rect.top >= containerRect.top && rect.top <= containerRect.top + 100) {
                const pageNumber = parseInt(wrapper.id.replace('page-', ''));
                if (pageNumber !== currentPage.value) {
                    currentPage.value = pageNumber;
                    emit('page-changed', pageNumber);
                }
                break;
            }
        }
    };

    onMounted(() => {
        loadPdf();

        // 添加事件监听
        document.addEventListener('keydown', handleKeyPress);
        if (mainContent.value) {
            mainContent.value.addEventListener('scroll', handleScroll);
        }
    });

    // 暴露方法给父组件
    defineExpose({
        goToPage,
        prevPage,
        nextPage,
        addHighlight,
        currentPage,
        totalPages
    });
</script>

<style scoped>
    .pdf-viewer-container {
        height: 100vh;
        display: flex;
        flex-direction: column;
        background: #f8f9fa;
    }

    .navigation-controls {
        background: white;
        padding: 12px 20px;
        border-bottom: 1px solid #e9ecef;
        display: flex;
        align-items: center;
        gap: 20px;
        flex-wrap: wrap;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        z-index: 100;
    }

    .page-info {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .nav-btn {
        padding: 8px 12px;
        border: 1px solid #ddd;
        background: white;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.2s;
    }

    .nav-btn:hover:not(:disabled) {
        background: #007bff;
        color: white;
        border-color: #007bff;
    }

    .nav-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .page-counter {
        font-weight: 500;
        color: #495057;
        min-width: 120px;
        text-align: center;
    }

    .page-jump {
        display: flex;
        gap: 8px;
        align-items: center;
    }

    .page-input {
        width: 80px;
        padding: 8px;
        border: 1px solid #ddd;
        border-radius: 4px;
        text-align: center;
    }

    .jump-btn {
        padding: 8px 12px;
        background: #28a745;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
    }

    .jump-btn:hover {
        background: #218838;
    }

    .highlight-jump {
        flex: 1;
        min-width: 200px;
    }

    .highlight-select {
        width: 100%;
        padding: 8px;
        border: 1px solid #ddd;
        border-radius: 4px;
        background: white;
    }

    .pdf-content {
        flex: 1;
        display: flex;
        overflow: hidden;
    }

    .thumbnail-sidebar {
        width: 120px;
        background: white;
        border-right: 1px solid #e9ecef;
        overflow-y: auto;
        padding: 10px;
    }

    .thumbnail {
        margin-bottom: 10px;
        cursor: pointer;
        border: 2px solid transparent;
        border-radius: 4px;
        padding: 4px;
        transition: all 0.2s;
    }

    .thumbnail:hover {
        border-color: #007bff;
    }

    .thumbnail.active {
        border-color: #007bff;
        background: #e7f3ff;
    }

    .thumbnail-canvas {
        background: #f8f9fa;
        border: 1px solid #dee2e6;
        border-radius: 2px;
        overflow: hidden;
    }

    .thumbnail-label {
        display: block;
        text-align: center;
        font-size: 11px;
        color: #6c757d;
        margin-top: 4px;
    }

    .main-content {
        flex: 1;
        overflow-y: auto;
        padding: 20px;
        background: #f8f9fa;
    }

    .page-wrapper {
        background: white;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        margin-bottom: 20px;
        transition: all 0.3s ease;
        border: 2px solid transparent;
    }

    .page-wrapper.current {
        border-color: #007bff;
        box-shadow: 0 4px 12px rgba(0,123,255,0.2);
    }

    .page-header {
        padding: 10px 20px;
        border-bottom: 1px solid #e9ecef;
        background: #f8f9fa;
        border-radius: 8px 8px 0 0;
    }

    .page-number-badge {
        background: #007bff;
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 500;
    }

    .page-content {
        position: relative;
        padding: 20px;
    }

    .highlight-layer {
        position: absolute;
        top: 20px;
        left: 20px;
        pointer-events: none;
    }

    .text-highlight {
        pointer-events: auto;
    }

    .floating-controls {
        position: fixed;
        right: 20px;
        bottom: 20px;
        background: white;
        border-radius: 50px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        display: flex;
        align-items: center;
        gap: 5px;
        padding: 10px;
        z-index: 1000;
    }

    .floating-btn {
        width: 40px;
        height: 40px;
        border: none;
        background: transparent;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        transition: all 0.2s;
    }

    .floating-btn:hover {
        background: #007bff;
        color: white;
    }

    .page-display {
        padding: 0 12px;
        font-weight: 600;
        color: #007bff;
        min-width: 40px;
        text-align: center;
    }

    /* 滚动条样式 */
    .main-content::-webkit-scrollbar {
        width: 8px;
    }

    .main-content::-webkit-scrollbar-track {
        background: #f1f1f1;
    }

    .main-content::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 4px;
    }

    .main-content::-webkit-scrollbar-thumb:hover {
        background: #a8a8a8;
    }
</style>